import { DestroyRef, ErrorHandler, inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AppConfigService } from '@markant-platform/shared/data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { TranslocoService } from '@jsverse/transloco';

@Injectable()
export class PlatformErrorHandler implements ErrorHandler {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly snackbarService: SnackbarService = inject(SnackbarService);
  private readonly translocoService: TranslocoService = inject(TranslocoService);

  /**
   * Sends errors to Elastic via platform backend.
   * Ignore errors that are HTTP error responses.
   *
   * Logs all errors to the console.
   * @param {Error} error - The error to handle
   */
  handleError(error: Error): void {
    this.showSnackbarForErrorCodes(error);
    this.logErrorInElastic(error);

    console.error(error);
  }

  private showSnackbarForErrorCodes(error: Error): void {
    if (error instanceof HttpErrorResponse && error.error.errorCode) {
      const errorTranslationKey = `error.${error.error.errorCode.replace('-', '.')}`;
      const translation = error.error.errorCode.endsWith('999')
        ? this.translocoService.translate(errorTranslationKey, { errorMessage: error.error.message })
        : this.translocoService.translate(errorTranslationKey);

      if (translation !== errorTranslationKey) {
        this.snackbarService.showErrorSnackbar(`${translation} (TraceId: ${error.error.traceId})`);
      }
    }
  }

  private logErrorInElastic(error: Error): void {
    if (!(error instanceof HttpErrorResponse)) {
      const message = error.stack ?? error.message;

      this.httpClient
        .post(AppConfigService.appConfiguration.apiUrl['logErrorService'], { message })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }
  }
}
